import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Avatar,
  Typography,
} from '@mui/material';
import {
  MoreHorizontal as MoreHorizontalIcon,
  Edit as EditIcon,
  Trash2 as DeleteIcon,
  UserPlus as PersonAddIcon,
  ChevronDown as UnfoldMoreIcon,
  ExternalLink as ArrowOutwardIcon,
  Users as WorkspacesIcon,
  Building2 as BusinessIcon,
  Users as PeopleAltIcon,
} from 'lucide-mui';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { Member } from '../../types';
import { TeamSpace } from '../../types/state';
import { pushModal } from '../globalModals/globalModalsSlice';
import {
  removeTeamMemberThunk,
  scheduleTeamDeletionThunk,
  selectTeam,
} from '../team/teamSlice';
import { setAlertMessage } from '../dashboard/dashboardSlice';
import { SettingsSection } from './common';
import { stringAvatar } from '../../lib/textUtils';

const TeamSettings: React.FC = () => {
  const dispatch = useDispatch();
  const { selectedTeam, teams } = useSelector((state: RootState) => ({
    selectedTeam: state.team?.selectedTeam,
    teams: state.team?.teams,
  }));
  const { teamSpaceMembers = [] } = (selectedTeam as TeamSpace) || {};

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isDeleteTeamDialogOpen, setDeleteTeamDialogOpen] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [
    teamSelectorAnchorEl,
    setTeamSelectorAnchorEl,
  ] = useState<null | HTMLElement>(null);

  const handleOpenModal = () => {
    dispatch(pushModal({ type: 'invite-by-link-modal' }));
  };

  const handleRemove = (memberId: string) => {
    if (selectedTeam) {
      const { teamId } = selectedTeam;
      dispatch(removeTeamMemberThunk({ teamId, userId: memberId }));
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleRename = () => {
    handleMenuClose();
    // Implement rename functionality
  };

  const handleDelete = () => {
    setDeleteTeamDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteTeam = async () => {
    if (confirmationText === 'DELETE') {
      await dispatch(scheduleTeamDeletionThunk(selectedTeam?.teamId!));
      dispatch(
        setAlertMessage({
          type: 'success',
          message: `Your team '${selectedTeam?.name}' has been deleted successfully. You can restore it within 14 days.`,
        })
      );
      setDeleteTeamDialogOpen(false);
    } else {
      dispatch(
        setAlertMessage({
          type: 'error',
          message: 'Please type "DELETE" to confirm.',
        })
      );
      setConfirmationText('');
      setDeleteTeamDialogOpen(false);
    }
  };

  const getStatus = (member: Member) => {
    if (!member.inviteAccepted && member.unregistered) {
      return 'Unregistered';
    }
    if (!member.inviteAccepted) {
      return 'Pending';
    }
    if (member.status) {
      return 'Active';
    }
    return 'Disabled';
  };

  const isLastTeam =
    teams.filter((team) => !team.deleteRequestedAt).length <= 1;

  if (!selectedTeam) {
    return null;
  }

  const handleTeamSelect = (teamId: string) => {
    const newSelectedTeam = teams.find((t) => t.teamId === teamId);
    if (!newSelectedTeam) return;
    dispatch(selectTeam(newSelectedTeam));
  };

  const handleTeamSelectorClick = (event: React.MouseEvent<HTMLElement>) => {
    setTeamSelectorAnchorEl(event.currentTarget);
  };

  const handleTeamSelectorClose = () => {
    setTeamSelectorAnchorEl(null);
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        overflow: 'auto',
        bgcolor: 'transparent',
        px: 3,
        pt: 1,
        pb: 3,
        '& > *:not(:last-child)': {
          mb: 3,
        },
      }}
    >
      <SettingsSection title="Team Management">
        <Box sx={{ py: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 },
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ flex: 1, mr: { xs: 0, sm: 3 }, width: '100%' }}>
              <Box sx={{ display: 'flex' }}>
                <Box
                  sx={{
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    alignSelf: 'center',
                    mr: 2.5,
                    height: '42px',
                    '& > svg': {
                      width: 16,
                      height: 16,
                    },
                  }}
                >
                  <WorkspacesIcon
                    sx={{ width: '20px !important', height: '20px !important' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Current Team
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Select and manage your current workspace
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                onClick={handleTeamSelectorClick}
                endIcon={<UnfoldMoreIcon sx={{ width: 16, height: 16 }} />}
                sx={{
                  width: { xs: '100%', sm: 200 },
                  justifyContent: 'space-between',
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  px: 2,
                  py: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    sx={{
                      ...stringAvatar(selectedTeam.name).sx,
                      bgcolor: 'primary.main',
                      width: 24,
                      height: 24,
                      fontSize: '0.75rem',
                    }}
                    variant="rounded"
                  >
                    {stringAvatar(selectedTeam.name).children}
                  </Avatar>
                  <Typography sx={{ ml: 1 }}>{selectedTeam.name}</Typography>
                </Box>
              </Button>
              <Menu
                anchorEl={teamSelectorAnchorEl}
                open={Boolean(teamSelectorAnchorEl)}
                onClose={handleTeamSelectorClose}
                PaperProps={{
                  sx: {
                    width: { xs: '100%', sm: 200 },
                    maxWidth: '100%',
                    mt: 1,
                  },
                }}
              >
                {teams.map(({ name, teamId }) => {
                  const { sx, children } = stringAvatar(name);
                  const isSelected = selectedTeam?.teamId === teamId;
                  return (
                    <MenuItem
                      key={teamId}
                      onClick={() => {
                        handleTeamSelect(teamId);
                        handleTeamSelectorClose();
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 1,
                      }}
                    >
                      <Avatar
                        sx={{
                          ...sx,
                          bgcolor: isSelected
                            ? 'primary.main'
                            : 'secondary.dark',
                          width: 24,
                          height: 24,
                          fontSize: '0.75rem',
                        }}
                        variant="rounded"
                      >
                        {children}
                      </Avatar>
                      <Typography>{name}</Typography>
                      {isSelected && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuClick(e);
                          }}
                          sx={{ ml: 'auto' }}
                        >
                          <MoreHorizontalIcon sx={{ width: 16, height: 16 }} />
                        </IconButton>
                      )}
                    </MenuItem>
                  );
                })}
              </Menu>
            </Box>
          </Box>
        </Box>

        <Box sx={{ py: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 },
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ flex: 1, mr: { xs: 0, sm: 3 }, width: '100%' }}>
              <Box sx={{ display: 'flex' }}>
                <Box
                  sx={{
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    alignSelf: 'center',
                    mr: 2.5,
                    height: '42px',
                    '& > svg': {
                      width: 16,
                      height: 16,
                    },
                  }}
                >
                  <BusinessIcon
                    sx={{ width: '20px !important', height: '20px !important' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Team Plan
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Learn more about what you can do with team collaboration
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                color="secondary"
                href="https://forms.gle/hu9oPT7nGYJAiBud6"
                target="_blank"
                rel="noopener"
                endIcon={<ArrowOutwardIcon sx={{ width: 16, height: 16 }} />}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Talk to Sales
              </Button>
            </Box>
          </Box>
        </Box>
      </SettingsSection>

      <SettingsSection title="User Management">
        <TableContainer
          sx={{
            width: '100%',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Table sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleAltIcon sx={{ width: 16, height: 16 }} />
                    Name
                  </Box>
                </TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teamSpaceMembers.map((member) => (
                <TableRow key={member._id}>
                  <TableCell>{member.name}</TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={member.role}
                      size="small"
                      sx={{
                        bgcolor: 'action.selected',
                        color: 'text.primary',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatus(member)}
                      size="small"
                      sx={{
                        bgcolor: member.status
                          ? 'success.light'
                          : 'warning.light',
                        color: member.status ? 'success.dark' : 'warning.dark',
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(member._id)}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon sx={{ width: 16, height: 16 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PersonAddIcon sx={{ width: 16, height: 16 }} />}
            onClick={handleOpenModal}
          >
            Add Users
          </Button>
        </Box>
      </SettingsSection>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleRename}>
          <ListItemIcon>
            <EditIcon sx={{ width: 16, height: 16 }} />
          </ListItemIcon>
          <ListItemText>Rename Team</ListItemText>
        </MenuItem>
        {!isLastTeam && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon sx={{ width: 16, height: 16 }} />
            </ListItemIcon>
            <ListItemText>Delete Team</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={isDeleteTeamDialogOpen}
        onClose={() => setDeleteTeamDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: 400,
          },
        }}
      >
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ mb: 2 }}>Delete Team?</Box>
            <Box sx={{ color: 'text.secondary' }}>
              Deleting <strong>{selectedTeam?.name}</strong> will permanently
              erase all its data after 14 days. You can cancel the deletion
              within this period. Are you sure you want to proceed?
            </Box>
          </Box>
          <TextField
            variant="outlined"
            label="Type DELETE to confirm"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDeleteTeamDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteTeam}>
            Delete Team
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamSettings;
